import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { TypedLoginForm } from "./components/TypedLoginForm";
import { AcceptInvitationPage } from "./components/ParentManagement";
import { EmailVerificationPage } from "./components/EmailVerificationPage";
import { ResetPasswordPage } from "./welcome/ResetPasswordPage";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { setChildSessionToken, device, type ChildSessionResponse } from "./api";
import { WelcomeScreen } from "./welcome/WelcomeScreen";
import { LandingPage } from "./welcome/LandingPage";
import { LoginScreen } from "./welcome/LoginScreen";
import { SignupFlow } from "./signup/SignupFlow";
import { unauthViewFor, type UnauthView } from "./unauthView";
import { DeviceProfileCache } from "./welcome/DeviceProfileCache";
import { getSilhouetteUrls } from "./avatar/avatarUrlResolver";
import { useIsAdmin } from "./hooks/useIsAdmin";
import { PanelSurface } from "./chrome/PanelSurface";
import { ChromeTokens } from "./chrome/tokens";
import { SIGNUP_PATH, matchTopLevelRoute, shareProjectIdFromPath } from "./topLevelRoute";
import { signedInInApp, signedInShareTarget } from "./native/appLinks";
import { AccountDeleteWebPage } from "./erasure/AccountDeleteWebPage";
import { ReacceptTermsGate } from "./lifecycle/ReacceptTermsGate";
import { ensurePublicConfigLoaded } from "./lifecycle/useLifecycle";
import { consumeResumePoint } from "./boot/resumeRoute";
import { LegalModal } from "./legal/LegalModal";
import { LegalPage } from "./legal/LegalPage";
import type { LegalDocId } from "./legal/docs";
import { BillingChrome } from "./billing";
import { ChildSessionProvider } from "./hooks/useIsChildSession";
import { SubscribedPage } from "./billing/SubscribedPage";

// Route-level code splitting (ipad-native-shell R14 / task 14.2). Everything
// below is off the boot path: the signed-out screens (landing, login, signup)
// must paint without parsing Phaser, LiveKit, the canvas or the admin tools.
// AppShell carries the whole authenticated experience; it is prefetched after
// first paint (see prefetchAuthedShell) so signing in does not wait on it.
// Keep new heavy routes here, not in the static imports above — the CI budget
// (frontend/scripts/check-bundle-budget.mjs) fails the build if the boot
// chunk grows past Decision 9's limit.
const loadAppShell = () => import("./components/AppShell");
const AppShell = lazy(() => loadAppShell().then((m) => ({ default: m.AppShell })));
const AdminPresencePage = lazy(() => import("./pages/AdminPresencePage").then((m) => ({ default: m.AdminPresencePage })));
const AdminVerificationSupport = lazy(() => import("./pages/AdminVerificationSupport").then((m) => ({ default: m.AdminVerificationSupport })));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then((m) => ({ default: m.AdminDashboard })));
const AdminEntityCatalogPage = lazy(() => import("./pages/AdminEntityCatalogPage").then((m) => ({ default: m.AdminEntityCatalogPage })));
const ContentCalendarPage = lazy(() => import("./pages/ContentCalendarPage").then((m) => ({ default: m.ContentCalendarPage })));
const AdminWaitlistPage = lazy(() => import("./pages/AdminWaitlistPage").then((m) => ({ default: m.AdminWaitlistPage })));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })));
const AdminNotificationTemplatesPage = lazy(() => import("./pages/AdminNotificationTemplatesPage").then((m) => ({ default: m.AdminNotificationTemplatesPage })));
const AdminModerationPortal = lazy(() => import("./components/AdminModerationPortal").then((m) => ({ default: m.AdminModerationPortal })));
const BuildingTemplateListView = lazy(() => import("./components/admin/BuildingTemplateListView").then((m) => ({ default: m.BuildingTemplateListView })));
const BuildingTemplateEditorView = lazy(() => import("./components/admin/BuildingTemplateEditorView").then((m) => ({ default: m.BuildingTemplateEditorView })));
const EngagementQueuePage = lazy(() => import("./engagement/EngagementQueuePage").then((m) => ({ default: m.EngagementQueuePage })));
const AdminFrictionPage = lazy(() => import("./pages/AdminFrictionPage"));
const AdminSharePlaysPage = lazy(() => import("./pages/AdminSharePlaysPage"));
const AdminProjectKindsPage = lazy(() => import("./pages/AdminProjectKindsPage"));
const AdminOpportunitiesPage = lazy(() => import("./pages/AdminOpportunitiesPage"));
const EngagementSignalsPage = lazy(() => import("./pages/EngagementSignalsPage").then((m) => ({ default: m.EngagementSignalsPage })));
const AdminReportsPage = lazy(() => import("./adminReports/AdminReportsPage").then((m) => ({ default: m.AdminReportsPage })));
const CostUsagePage = lazy(() => import("./cost/CostUsagePage").then((m) => ({ default: m.CostUsagePage })));
const SessionExplorerPage = lazy(() => import("./pages/SessionExplorerPage").then((m) => ({ default: m.SessionExplorerPage })));
const SessionTrailPage = lazy(() => import("./pages/SessionTrailPage").then((m) => ({ default: m.SessionTrailPage })));
const BlogAdminListPage = lazy(() => import("./pages/BlogAdminListPage").then((m) => ({ default: m.BlogAdminListPage })));
const BlogAdminEditorPage = lazy(() => import("./pages/BlogAdminEditorPage").then((m) => ({ default: m.BlogAdminEditorPage })));
const ReviewQueuePage = lazy(() => import("./pages/ReviewQueuePage").then((m) => ({ default: m.ReviewQueuePage })));
const ReviewDetailPage = lazy(() => import("./pages/ReviewDetailPage").then((m) => ({ default: m.ReviewDetailPage })));
const BatchBoard = lazy(() => import("./review/BatchBoard").then((m) => ({ default: m.BatchBoard })));
const NarrationAdmin = lazy(() => import("./narration/NarrationAdmin").then((m) => ({ default: m.NarrationAdmin })));
const DevHarnessPage = lazy(() => import("./pages/DevHarnessPage").then((m) => ({ default: m.DevHarnessPage })));
const VwkRenderPage = lazy(() => import("./pages/VwkRenderPage").then((m) => ({ default: m.VwkRenderPage })));
const AnonymousProjectViewer = lazy(() => import("./share/AnonymousProjectViewer").then((m) => ({ default: m.AnonymousProjectViewer })));

/** Same blank ground the auth-loading states paint, so a lazy route resolving
 *  reads as one continuous load rather than a flash. */
const BOOT_GROUND = <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#c2d5db" }} />;

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={BOOT_GROUND}>{children}</Suspense>;
}

let authedShellPrefetched = false;
/** Warm the AppShell chunk once the first screen has painted — idle time on
 *  the login screen, so the post-sign-in render does not pay for the fetch.
 *  Parse still happens on demand; this only moves the network/disk read. */
function prefetchAuthedShell() {
  if (authedShellPrefetched) return;
  authedShellPrefetched = true;
  const go = () => { void loadAppShell().catch(() => { authedShellPrefetched = false; }); };
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (ric) ric(go, { timeout: 2000 }); else setTimeout(go, 200);
}

type ChildLoginMode = "voice" | "typed";

interface ChildLoginState {
  childId: string;
  childName: string;
  tenantId: string;
  mode: ChildLoginMode;
}

function AppContent() {
  const { isAuthenticated, loading, userId: authUserId, refreshSession, offline } = useAuth();
  // Kick off /config/public early so the vertical rollout flags (bug #22)
  // are hydrated by the time the create-project popover opens. Fire once at
  // mount — cached module-side so subsequent hooks reuse it.
  useEffect(() => {
    void ensurePublicConfigLoaded();
    prefetchAuthedShell();
  }, []);
  const [userId, setUserId] = useState("");
  const [childDisplayName, setChildDisplayName] = useState("");
  const [childLoginState, setChildLoginState] = useState<ChildLoginState | null>(null);
  const [unauthView, setUnauthView] = useState<UnauthView | undefined>(undefined);
  // Server-side device trust for the unauthenticated picker. null = still
  // resolving. Replaces the old localStorage DeviceProfileCache check.
  const [serverTrusted, setServerTrusted] = useState<boolean | null>(null);

  // When parent authenticates via useAuth, set userId from JWT
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (isAuthenticated && authUserId && !userId) {
      setUserId(authUserId);
    }
    // Only clear userId on parent logout (was authenticated, now isn't)
    if (wasAuthenticated.current && !isAuthenticated) {
      setUserId("");
      setChildDisplayName("");
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, authUserId, userId]);

  // Resolve server-side device trust for the unauth profile picker. Re-resolves
  // when isAuthenticated flips to false (logout) so a device trusted during the
  // session lands on the picker — not the landing — after signing out.
  useEffect(() => {
    if (isAuthenticated) return;
    let cancelled = false;
    device
      .profiles()
      .then((r) => { if (!cancelled) setServerTrusted(r.trusted && r.family.length > 0); })
      .catch(() => { if (!cancelled) setServerTrusted(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (loading) {
    // ipad-native-shell 2.8: the boot refresh could not reach us; the session
    // is kept and retried — say so rather than paint a blank screen.
    if (offline) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: ChromeTokens.color.sky }}>
          <p role="status" style={{ fontFamily: ChromeTokens.font.ui, fontSize: 18, fontWeight: 700, color: ChromeTokens.color.ink }}>
            Waiting for the internet…
          </p>
        </div>
      );
    }
    return BOOT_GROUND;
  }

  // Wait for userId to be set after auth completes before rendering the main app
  if (isAuthenticated && !userId) {
    return BOOT_GROUND;
  }

  // --- Dev-only chrome harness (dead code in prod via import.meta.env.DEV) ---
  if (import.meta.env.DEV && window.location.pathname.startsWith("/__dev")) {
    if (window.location.pathname.startsWith("/__dev/vwk-render")) {
      return <Lazy><VwkRenderPage /></Lazy>;
    }
    return <Lazy><DevHarnessPage /></Lazy>;
  }

  // --- Admin routes ---
  const adminPath = window.location.pathname.replace(/\/+$/, "") || "/";
  if (adminPath === "/admin/presence-audio") {
    return <AdminRoute><AdminPresencePage /></AdminRoute>;
  }
  if (adminPath === "/admin/waitlist") {
    return <AdminRoute><AdminWaitlistPage /></AdminRoute>;
  }
  if (adminPath === "/admin/users") {
    return <AdminRoute><AdminUsersPage /></AdminRoute>;
  }
  if (adminPath === "/admin/notification-templates") {
    return <AdminRoute><AdminNotificationTemplatesPage /></AdminRoute>;
  }
  if (adminPath === "/admin/narration") {
    return <AdminRoute><NarrationAdmin /></AdminRoute>;
  }
  if (adminPath === "/admin/verification-support") {
    return <AdminRoute><AdminVerificationSupport /></AdminRoute>;
  }
  if (adminPath === "/admin/projects") {
    return <AdminRoute><AdminModerationPortal /></AdminRoute>;
  }
  if (adminPath === "/admin/friction") {
    return <AdminRoute><AdminFrictionPage /></AdminRoute>;
  }
  if (adminPath === "/admin/share-plays") {
    return <AdminRoute><AdminSharePlaysPage /></AdminRoute>;
  }
  if (adminPath === "/admin/project-kinds") {
    return <AdminRoute><AdminProjectKindsPage /></AdminRoute>;
  }
  if (adminPath === "/admin/opportunities") {
    return <AdminRoute><AdminOpportunitiesPage /></AdminRoute>;
  }
  if (adminPath === "/admin/engagement") {
    return <AdminRoute><EngagementQueuePage /></AdminRoute>;
  }
  if (adminPath === "/admin/engagement-signals") {
    return <AdminRoute><EngagementSignalsPage /></AdminRoute>;
  }
  if (adminPath === "/admin/reports") {
    return <AdminRoute><AdminReportsPage /></AdminRoute>;
  }
  if (adminPath.startsWith("/admin/cost")) {
    return <AdminRoute><CostUsagePage /></AdminRoute>;
  }
  if (adminPath.startsWith("/admin/sessions")) {
    return <AdminSessionsRoute />;
  }
  if (adminPath.startsWith("/admin/building-templates")) {
    return <BuildingTemplatesRoute />;
  }
  if (adminPath.startsWith("/admin/journal")) {
    return <BlogAdminRoute />;
  }
  if (adminPath.startsWith("/admin/review")) {
    return <ReviewAdminRoute />;
  }
  if (adminPath === "/admin/calendar") {
    return <AdminRoute><ContentCalendarPage /></AdminRoute>;
  }
  if (adminPath === "/admin/entities") {
    return <AdminRoute><AdminEntityCatalogPage /></AdminRoute>;
  }
  if (adminPath === "/admin") {
    return <AdminRoute><AdminDashboard /></AdminRoute>;
  }

  // --- Child voice/typed login flow (works without parent auth) ---
  if (childLoginState) {
    const onChildSuccess = (session: ChildSessionResponse) => {
      setChildSessionToken(session.child_session_token);
      setUserId(session.child_id);
      setChildDisplayName(session.username);
      DeviceProfileCache.upsert({
        account_id: session.child_id,
        username: session.username,
        avatar_url: session.avatar_url || getSilhouetteUrls(session.child_id).portrait_128,
        account_type: "child",
        family_id: childLoginState.tenantId === "current" ? null : childLoginState.tenantId,
        last_used_at: Date.now(),
        trusted_on_this_device: false,
      });
      setChildLoginState(null);
    };

    return (
      <TypedLoginForm
        childName={childLoginState.childName}
        familyAccountId={childLoginState.tenantId}
        onSuccess={onChildSuccess}
        onCancel={() => setChildLoginState(null)}
      />
    );
  }

  // --- Unauthenticated flows ---
  if (!isAuthenticated && !userId) {
    // The main site: landing / login / signup / welcome-back flows, served at
    // the root path. (The waitlist page is retired; WaitlistLandingPage stays
    // in the tree for now but is no longer routed.)
    // After an explicit logout we prefer sign-in over the landing page (the
    // `logged_out` flag is set by useAuth.logout, cleared on any sign-in).
    let loggedOut = false;
    try { loggedOut = sessionStorage.getItem("logged_out") === "1"; } catch { /* SSR/test */ }
    // A navigation or ?view= renders at once; a share link's intent (picker on a trusted
    // device, else sign-up) and the default (picker on a trusted device, sign-in after
    // logout, Landing when fresh) wait on the trust check. The order is in unauthViewFor.
    const view = unauthViewFor({ navigated: unauthView, search: window.location.search, serverTrusted, loggedOut });
    if (view === null) {
      return BOOT_GROUND;
    }

    if (view === "welcome-back") {
      return (
        <WelcomeScreen
          onAuthenticated={(session) => {
            if (session.type === "child") {
              setUserId(session.childId);
              setChildDisplayName(session.displayName);
            }
          }}
          onNewPerson={() => setUnauthView("login")}
          onSignup={() => setUnauthView("signup")}
        />
      );
    }

    if (view === "landing") {
      return (
        <LandingPage
          onTryIt={() => setUnauthView("signup")}
          onSignup={() => setUnauthView("signup")}
          onLogin={() => setUnauthView("login")}
          returning={!!serverTrusted}
          onProfiles={() => setUnauthView("welcome-back")}
        />
      );
    }

    if (view === "signup") {
      return (
        <SignupFlow
          onComplete={() => { refreshSession(); }}
          onBackToLogin={() => setUnauthView("login")}
        />
      );
    }

    // view === "login"
    return (
      <LoginScreen
        onAuthenticated={(session) => {
          if (session.type === "child") {
            setUserId(session.childId);
            setChildDisplayName(session.displayName);
          }
        }}
        onSignup={() => setUnauthView("signup")}
      />
    );
  }

  // --- Authenticated: straight to AppShell ---
  // Parent/individual: childId is empty → AppShell renders in parent mode
  // Child (via voice/typed login): childId is set → AppShell renders in child mode
  // A deep link wins; otherwise, after iOS killed and reloaded the WebView,
  // reopen what the child had open (boot/resumeRoute.ts, task 16.4).
  const returnTo =
    new URLSearchParams(window.location.search).get("return_to") || consumeResumePoint(userId);
  return (
    <AuthedShell userId={userId} childDisplayName={childDisplayName} returnTo={returnTo} />
  );
}

/**
 * AuthedShell — wraps AppShell with the launch-compliance / launch-ux-
 * followups chrome that has to appear above whichever view is active:
 *
 *  - The account-notice layer (trial / subscription / deletion grace) is
 *    NOT mounted here — each shell lays its row out above its own top bar so
 *    it occupies a real slot instead of overlaying one. See ``NoticeRow``.
 *  - ReacceptTermsGate — adults see a blocking modal when the platform
 *    Terms version exceeds the tenant's last-accepted; kids see a
 *    "show a grown-up" interstitial.
 *  - LegalModal — opened by ReacceptTermsGate's "Read the Terms /
 *    Privacy" links; also reusable by other chrome.
 */
function AuthedShell({
  userId,
  childDisplayName,
  returnTo,
}: {
  userId: string;
  childDisplayName: string | null;
  returnTo: string | undefined;
}) {
  const [legalDoc, setLegalDoc] = useState<LegalDocId | null>(null);
  // The paper texture is the signed-in chrome's first-paint image (TopBar,
  // panels), but its first request comes from AppShell's stylesheet — i.e.
  // only after the lazy chunk has loaded. Start it now, in parallel with that
  // chunk. Same URL as the CSS, so the stylesheet hits the cache. Task 14.9.
  useEffect(() => {
    new Image().src = ChromeTokens.texture.paperGrainUrl;
  }, []);
  return (
    <>
      <ReacceptTermsGate onViewDoc={(d) => setLegalDoc(d)} />
      <ChildSessionProvider childDisplayName={childDisplayName}>
        <BillingChrome>
          <Lazy>
            <AppShell
              userId={userId}
              childDisplayName={childDisplayName ?? undefined}
              returnTo={returnTo}
            />
          </Lazy>
        </BillingChrome>
      </ChildSessionProvider>
      <LegalModal
        open={legalDoc !== null}
        doc={legalDoc ?? "privacy"}
        onClose={() => setLegalDoc(null)}
      />
    </>
  );
}

/**
 * Admin-gated route for the Building Template Editor.
 */
function BuildingTemplatesRoute() {
  const isAdmin = useIsAdmin();

  if (!isAdmin) {
    return (
      <PanelSurface open={true} onDismiss={() => { window.location.href = "/"; }} ariaLabel="Admin access required">
        <p style={{ textAlign: "center", margin: 0 }}>This area is for admins.</p>
      </PanelSurface>
    );
  }

  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/admin/building-templates") {
    return <Lazy><BuildingTemplateListView /></Lazy>;
  }
  if (path === "/admin/building-templates/new") {
    return <Lazy><BuildingTemplateEditorView /></Lazy>;
  }
  const editMatch = path.match(/^\/admin\/building-templates\/([^/]+)\/edit$/);
  if (editMatch) {
    return <Lazy><BuildingTemplateEditorView templateId={editMatch[1]} /></Lazy>;
  }
  return <Lazy><BuildingTemplateListView /></Lazy>;
}

/**
 * Admin-gated route for the Journal (blog) admin.
 *
 * Three sub-routes:
 *   /admin/journal              — list
 *   /admin/journal/new          — create
 *   /admin/journal/<id>/edit    — edit
 *
 * Backend enforces the editor permission (users.is_content_editor=true)
 * separately from is_admin — the page surfaces a clear "set
 * is_content_editor" instruction if the listing call 403s.
 */
function BlogAdminRoute() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <PanelSurface open={true} onDismiss={() => { window.location.href = "/"; }} ariaLabel="Admin access required">
        <p style={{ textAlign: "center", margin: 0 }}>This area is for admins.</p>
      </PanelSurface>
    );
  }
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/admin/journal" || path === "/admin/journal/") {
    return <Lazy><BlogAdminListPage /></Lazy>;
  }
  if (path === "/admin/journal/new") {
    return <Lazy><BlogAdminEditorPage /></Lazy>;
  }
  const editMatch = path.match(/^\/admin\/journal\/([^/]+)\/edit$/);
  if (editMatch) {
    return <Lazy><BlogAdminEditorPage itemId={editMatch[1]} /></Lazy>;
  }
  return <Lazy><BlogAdminListPage /></Lazy>;
}

/**
 * Content-review routes — editor surface for AI-generated drafts.
 *
 *   /admin/review              — queue listing
 *   /admin/review/<id>         — item detail + approve/reject/send-back
 *
 * Backend enforces users.is_content_editor=true (separate from is_admin).
 * The page surfaces a clear instruction when the API 403s.
 */
function ReviewAdminRoute() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <PanelSurface open={true} onDismiss={() => { window.location.href = "/"; }} ariaLabel="Admin access required">
        <p style={{ textAlign: "center", margin: 0 }}>This area is for admins.</p>
      </PanelSurface>
    );
  }
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/admin/review" || path === "/admin/review/") {
    return <Lazy><ReviewQueuePage /></Lazy>;
  }
  // The compare board must match BEFORE the detail route — "batch" would
  // otherwise be read as an item id and 404 against the detail endpoint.
  const batchMatch = path.match(/^\/admin\/review\/batch\/([^/]+)$/);
  if (batchMatch) {
    return <Lazy><BatchBoard sourceId={batchMatch[1]} /></Lazy>;
  }
  const detailMatch = path.match(/^\/admin\/review\/([^/]+)$/);
  if (detailMatch) {
    return <Lazy><ReviewDetailPage itemId={detailMatch[1]} /></Lazy>;
  }
  return <Lazy><ReviewQueuePage /></Lazy>;
}

/**
 * Session explorer + trail live under /admin/sessions with a dynamic id, so they
 * need a startsWith wrapper rather than an exact-match branch.
 */
function AdminSessionsRoute() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <PanelSurface open={true} onDismiss={() => { window.location.href = "/"; }} ariaLabel="Admin access required">
        <p style={{ textAlign: "center", margin: 0 }}>This area is for admins.</p>
      </PanelSurface>
    );
  }
  const path = window.location.pathname.replace(/\/+$/, "");
  const trailMatch = path.match(/^\/admin\/sessions\/([^/]+)\/trail$/);
  if (trailMatch) {
    return <Lazy><SessionTrailPage sessionId={trailMatch[1]} /></Lazy>;
  }
  return <Lazy><SessionExplorerPage /></Lazy>;
}

/**
 * Generic admin-gated wrapper.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useIsAdmin();

  if (!isAdmin) {
    return (
      <PanelSurface open={true} onDismiss={() => { window.location.href = "/"; }} ariaLabel="Admin access required">
        <p style={{ textAlign: "center", margin: 0 }}>This area is for admins.</p>
      </PanelSurface>
    );
  }

  return <Lazy>{children}</Lazy>;
}

function App() {
  // Match the top-level route tolerating trailing slashes. Amplify/CloudFront
  // 301-redirects extensionless paths to add a trailing slash (e.g.
  // /verify-email?token=X → /verify-email/?token=X). A naive
  // pathname === "/verify-email" check fails for the redirected form, falls
  // through to the default app view, and silently breaks email verification
  // (the token is never submitted). matchTopLevelRoute normalizes the slash.
  // See frontend/src/topLevelRoute.ts and failure-patterns.md Pattern A.
  const route = matchTopLevelRoute(window.location.pathname);

  // The demo closed 2026-11-01. Old links and bookmarks go to the free trial.
  if (route === "demo") {
    window.location.replace(SIGNUP_PATH);
    return BOOT_GROUND;
  }

  if (route === "accept-invitation") {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || "";
    return <AcceptInvitationPage token={token} />;
  }

  if (route === "verify-email") {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || null;
    return <EmailVerificationPage token={token} />;
  }

  if (route === "reset-password") {
    return <ResetPasswordPage />;
  }

  if (route === "account-delete") {
    // /account/delete is the public deletion path Google Play (and
    // Apple post-5.1.1(v)) require. The page is auth-less: the parent
    // enters their email, we send a single-use confirmation link, and
    // they finish at /account/delete/confirm?token=…. No password is
    // ever asked for here, by design.
    return <AccountDeleteWebPage />;
  }

  if (route === "account-delete-confirm") {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    return <AccountDeleteWebPage initialToken={token} />;
  }

  if (route === "subscribed") {
    // Synthetic landing for Google Ads conversion tracking. Reached only via
    // a hard nav from BillingChrome after a paid subscribe (no auth needed
    // to render — the page just shows the celebratory copy + Continue CTA).
    return <SubscribedPage />;
  }

  // /privacy and /terms — public legal deep-links. Handled here at the
  // App() top level (before the auth providers) so the policy text is
  // readable by logged-out visitors and app-store reviewers alike. The
  // page opens the Legal Center directly to the requested doc.
  if (route === "privacy") {
    return <LegalPage doc="privacy" />;
  }

  if (route === "terms") {
    return <LegalPage doc="terms" />;
  }

  // /share/<id> — auth-less anonymous shareable-link viewer.
  //
  // In production, CloudFront routes `/share/<id>` to the Astro app's SSR
  // Lambda BEFORE Vite sees the request (see `topLevelRoute.ts` comment),
  // so this branch never fires in prod. Locally (Vite dev server), there's
  // no equivalent routing, so without this fallback the SPA falls through
  // to AppShell which requires auth → login page. This branch makes share
  // links work in local dev.
  //
  // In the app it does fire (the bundle serves every path), and someone signed
  // in goes to the signed-in viewer in their own town instead (operator ruling
  // 2026-10-21, native/appLinks.ts `signedInShareTarget`). The URL is rewritten
  // in place so the world below reads it; nothing reloads.
  if (shareProjectIdFromPath(window.location.pathname)) {
    const target = signedInShareTarget(window.location.pathname, signedInInApp());
    if (!target) return <Lazy><AnonymousProjectViewer /></Lazy>;
    window.history.replaceState(null, "", target);
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
